import React, { useState, useEffect, type ReactNode, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import { PhotoModalContext, type PhotoModalContextType } from './usePhotoModalContext';

/** Returns true if the pathname is a profile-based route */
const isProfileRoute = (pathname: string): boolean => pathname.startsWith('/profile');

import type { GridPhotoMetadata } from '@/components/organisms/GridNavigationPhotoModal';

interface PhotoModalProviderProps {
  children: ReactNode;
}

export const PhotoModalProvider: React.FC<PhotoModalProviderProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const [isOpen, setIsOpen] = useState(false);
  const [photoId, setPhotoId] = useState<string | null>(null);
  const [postId, setPostId] = useState<string | null>(null);
  const [username, setUsername] = useState<string | null>(null);
  const [shouldFocusComment, setShouldFocusComment] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [totalPhotos, setTotalPhotos] = useState(1);
  const [photoIds, setPhotoIds] = useState<string[]>([]);
  const [photoPostIds, setPhotoPostIds] = useState<string[]>([]); // Store post IDs for each photo
  const [isSubtabMode, setIsSubtabMode] = useState(false); // Track if we're in subtab navigation mode
  const [navigationContext, setNavigationContext] = useState<'post' | 'grid' | null>(null);
  const [gridContextType, setGridContextType] = useState<string | null>(null);
  const [gridPhotosData, setGridPhotosData] = useState<GridPhotoMetadata[] | null>(null);
  const [targetCommentId, setTargetCommentId] = useState<string | null>(null);
  const [highlightCommentId, setHighlightCommentId] = useState<string | null>(null);
  const [autoExpandParentId, setAutoExpandParentId] = useState<string | null>(null);
  const [isInPlaceMode, setIsInPlaceMode] = useState(false);
  
  // Track in-place mode to prevent URL monitoring from responding
  const isInPlaceModeRef = useRef(false);
  // Guard to prevent URL watcher from re-opening modal during close
  const isClosingRef = useRef(false);
  
  // Track the route where the modal was opened from (origin-aware closing)
  const originRouteRef = useRef<string | null>(null);
  
  // Track last processed location to prevent unnecessary updates
  const lastProcessedLocationRef = useRef<string>('');
  const isInternalNavigationRef = useRef(false);
  const pendingUrlUpdateRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const locationRef = useRef(location);  // Store location in ref to avoid re-renders
  
  // Refs to capture stable values for closeModal (prevent dependency issues)
  const usernameRef = useRef<string | null>(null);
  const navigationContextRef = useRef<'post' | 'grid' | null>(null);
  const gridContextTypeRef = useRef<string | null>(null);

  // Update location ref when location changes
  useEffect(() => {
    locationRef.current = location;
  }, [location]);
  
  // Keep refs in sync with state for stable closeModal values
  useEffect(() => {
    usernameRef.current = username;
    navigationContextRef.current = navigationContext;
    gridContextTypeRef.current = gridContextType;
  }, [username, navigationContext, gridContextType]);
  
  // Only handle browser back/forward navigation and initial page load
  useEffect(() => {
    // Skip if this is navigation with skipEffect flag
    if (location.state?.skipEffect) {
      return;
    }
    
    // Skip if this is programmatic navigation (openModal/closeModal/navigateToPhoto)
    if (isInternalNavigationRef.current || isClosingRef.current) {
      return;
    }
    
    const currentLocationKey = `${location.pathname}${location.search}`;
    
    // Skip if we've already processed this exact location
    if (lastProcessedLocationRef.current === currentLocationKey) {
      return;
    }
    
    const pathParts = location.pathname.split('/').filter(Boolean);
    const searchParams = new URLSearchParams(location.search);
    const action = searchParams.get('action');
    const photoIdFromQuery = searchParams.get('photo');
    
    // --- /square modal query-param patterns ---
    // Pattern: /square?modal=photo&photo=X&post=Y&user=Z
    if (pathParts[0] === 'square' && searchParams.get('modal') === 'photo') {
      // Skip if in-place mode and this is internal nav (we already set state in openModal)
      if (isInPlaceModeRef.current) {
        lastProcessedLocationRef.current = currentLocationKey;
        return;
      }
      
      const photoFromUrl = searchParams.get('photo');
      const postFromUrl = searchParams.get('post');
      const userFromUrl = searchParams.get('user');
      const ctxFromUrl = searchParams.get('ctx') as 'post' | 'grid' | null;
      const gctxFromUrl = searchParams.get('gctx');
      
      if (photoFromUrl && userFromUrl) {
        isInPlaceModeRef.current = true;
        setIsInPlaceMode(true);
        setUsername(userFromUrl);
        setPhotoId(photoFromUrl);
        setPostId(postFromUrl);
        setNavigationContext(ctxFromUrl || 'post');
        setGridContextType(gctxFromUrl);
        setIsOpen(true);
        setShouldFocusComment(action === 'comment');
        setTargetCommentId(null);
        setHighlightCommentId(null);
        setAutoExpandParentId(null);
        lastProcessedLocationRef.current = currentLocationKey;
        return;
      }
    }
    
    // /square with no modal param → close if open (browser back from modal)
    if (pathParts[0] === 'square' && !searchParams.get('modal') && isOpen && isInPlaceModeRef.current) {
      setIsOpen(false);
      setPhotoId(null);
      setPostId(null);
      setUsername(null);
      setNavigationContext(null);
      setGridContextType(null);
      setGridPhotosData(null);
      setShouldFocusComment(false);
      setPhotoIds([]);
      setPhotoPostIds([]);
      setIsSubtabMode(false);
      setCurrentPhotoIndex(0);
      setTotalPhotos(1);
      setTargetCommentId(null);
      setHighlightCommentId(null);
      setAutoExpandParentId(null);
      setIsInPlaceMode(false);
      isInPlaceModeRef.current = false;
      originRouteRef.current = null;
      lastProcessedLocationRef.current = currentLocationKey;
      return;
    }
    
    // Skip profile route URL monitoring if in-place mode
    if (isInPlaceModeRef.current) {
      return;
    }
    
    // --- Profile route patterns ---
    // Pattern: /profile/username/photos?photo=PHOTO_ID&context=avatars|all (grid navigation)
    const isGridPhotoRoute = 
      pathParts.length === 3 && 
      pathParts[0] === 'profile' && 
      pathParts[2] === 'photos' &&
      photoIdFromQuery;
    
    // Pattern: /profile/username/post/POST_ID?photo=PHOTO_ID (multi-photo posts)
    const isMultiPhotoPostRoute = 
      pathParts.length === 4 && 
      pathParts[0] === 'profile' && 
      pathParts[2] === 'post' &&
      photoIdFromQuery;
    
    // Pattern: /profile/username/post/POST_ID/photo/PHOTO_ID (single-photo posts)
    const isSinglePhotoPostRoute = 
      pathParts.length === 6 && 
      pathParts[0] === 'profile' && 
      pathParts[2] === 'post' &&
      pathParts[4] === 'photo';
    
    // Handle grid photo route
    if (isGridPhotoRoute) {
      const usernameFromUrl = pathParts[1];
      const gridContext = searchParams.get('context') || 'posted';
      
      if (import.meta.env.VITE_DEPLOY_ENV !== 'production') {
        logger.debug('[PhotoModalContext] Grid route detected:', {
          pathname: location.pathname,
          username: usernameFromUrl,
          photoId: photoIdFromQuery,
          gridContext,
          isInternalNav: isInternalNavigationRef.current
        });
      }
      
      // Skip if this is internal navigation (we already set state)
      if (isInternalNavigationRef.current) {
      if (import.meta.env.VITE_DEPLOY_ENV !== 'production') {
        logger.debug('[PhotoModalContext] Skipping - internal navigation');
      }
        return;
      }
      
      // Only process if this is external navigation (browser back/forward)
      if (usernameFromUrl && photoIdFromQuery) {
        setUsername(usernameFromUrl);
        setPhotoId(photoIdFromQuery);
        setPostId(null);
        setNavigationContext('grid');
        setGridContextType(gridContext);
        setIsOpen(true);
        setShouldFocusComment(action === 'comment');
        lastProcessedLocationRef.current = currentLocationKey;
      }
    }
    // Handle post photo routes on browser navigation or initial load
    else if (isMultiPhotoPostRoute || isSinglePhotoPostRoute) {
      const usernameFromUrl = pathParts[1];
      const postIdFromUrl = pathParts[3];
      const photoIdFromUrl = isSinglePhotoPostRoute ? pathParts[5] : photoIdFromQuery;
      
      if (import.meta.env.VITE_DEPLOY_ENV !== 'production') {
        logger.debug('[PhotoModalContext] Post photo route detected:', {
          pathname: location.pathname,
          isInitialLoad: !isOpen,
          username: usernameFromUrl,
          postId: postIdFromUrl,
          photoId: photoIdFromUrl,
          action,
          isInternalNav: isInternalNavigationRef.current
        });
      }
      
      // Skip if this is internal navigation (we already set state)
      if (isInternalNavigationRef.current) {
      if (import.meta.env.VITE_DEPLOY_ENV !== 'production') {
        logger.debug('[PhotoModalContext] Skipping - internal navigation');
      }
        return;
      }
      
      // Only process if this is external navigation (browser back/forward)
      if (usernameFromUrl && postIdFromUrl && photoIdFromUrl) {
        // Only update if values have changed
        setUsername(usernameFromUrl);
        setPostId(postIdFromUrl);
        setPhotoId(photoIdFromUrl);
        setNavigationContext('post');
        setGridContextType(null);
        setIsOpen(true);
        setShouldFocusComment(action === 'comment');
        lastProcessedLocationRef.current = currentLocationKey;
      }
    } else if (pathParts[0] === 'profile' && !isGridPhotoRoute && !isMultiPhotoPostRoute && !isSinglePhotoPostRoute) {
      // We're on a profile page but NOT on a photo route
      // This means we've navigated away from a photo modal
      
      // Skip if this is internal navigation (we already cleared state in closeModal)
      if (isInternalNavigationRef.current) {
        if (import.meta.env.VITE_DEPLOY_ENV !== 'production') {
          logger.debug('[PhotoModalContext] Skipping close - internal navigation already handled');
        }
        lastProcessedLocationRef.current = currentLocationKey;
        return;
      }
      
      // Only clear state if modal is somehow still open (e.g., external navigation)
      if (isOpen) {
        if (import.meta.env.VITE_DEPLOY_ENV !== 'production') {
          logger.debug('[PhotoModalContext] Closing modal - navigated away from photo route');
        }
        setIsOpen(false);
        setPhotoId(null);
        setPostId(null);
        setUsername(null);
        setNavigationContext(null);
        setGridContextType(null);
        setShouldFocusComment(false);
      }
      
      lastProcessedLocationRef.current = currentLocationKey;
    }
  }, [location.pathname, location.search, location.state?.skipEffect, isOpen]);

  // Handle browser back/forward navigation separately
  useEffect(() => {
    const handlePopState = () => {
      // Clear internal navigation flag to allow URL monitoring to process
      isInternalNavigationRef.current = false;
      
      if (import.meta.env.VITE_DEPLOY_ENV !== 'production') {
        logger.debug('[PhotoModalContext] Popstate event - allowing URL monitoring to handle');
      }
    };
    
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const openModal = useCallback((username: string, photoId: string, postId?: string, action?: 'comment', isMultiPhoto?: boolean, photoIndex?: number, photoIds?: string[], photoPostIds?: string[], navContext?: 'post' | 'grid', gridContext?: string, gridPhotos?: GridPhotoMetadata[]) => {
    // Type guard: Reject non-string usernames
    if (!username || typeof username !== 'string') {
      if (import.meta.env.VITE_DEPLOY_ENV !== 'production') {
        logger.error('[PhotoModalContext] Invalid username provided to openModal:', {
          username,
          type: typeof username
        });
      }
      return;
    }
    
    // Capture origin route
    const currentPath = locationRef.current.pathname;
    originRouteRef.current = currentPath;
    
    if (import.meta.env.VITE_DEPLOY_ENV !== 'production') {
      logger.debug('[PhotoModalContext] openModal called:', {
        username,
        photoId,
        postId,
        hasPostId: !!postId,
        isMultiPhoto,
        photoIndex,
        photoIds,
        photoPostIds,
        originRoute: currentPath,
        urlType: isMultiPhoto ? 'query-param' : 'hierarchical'
      });
    }

    // Set ALL state immediately
    setIsOpen(true);
    setUsername(username);
    setPhotoId(photoId);
    setShouldFocusComment(action === 'comment');
    setNavigationContext(navContext || 'post');
    setGridContextType(gridContext || null);
    setGridPhotosData(gridPhotos || null);

    // Reset highlight state from any previous modal session
    setTargetCommentId(null);
    setHighlightCommentId(null);
    setAutoExpandParentId(null);
    
    // Set photo IDs immediately if provided (for multi-photo posts or subtab mode)
    if (photoIds && photoIds.length > 0) {
      setPhotoIds(photoIds);
      setTotalPhotos(photoIds.length);
      
      // Set post IDs if provided (for subtab navigation)
      if (photoPostIds && photoPostIds.length > 0) {
        setPhotoPostIds(photoPostIds);
        setIsSubtabMode(true);
        // Update postId to match the current photo's post
        if (typeof photoIndex === 'number' && photoPostIds[photoIndex]) {
          postId = photoPostIds[photoIndex];
        }
      } else {
        setIsSubtabMode(false);
      }
    }
    
    // Set postId and index immediately
    if (postId) {
      setPostId(postId);
    }
    if (typeof photoIndex === 'number') {
      setCurrentPhotoIndex(photoIndex);
    }
    
    // If NOT on a profile route (e.g. /square), open in-place with query params
    if (!isProfileRoute(currentPath)) {
      if (import.meta.env.VITE_DEPLOY_ENV !== 'production') {
        logger.debug('[PhotoModalContext] Non-profile origin, opening in-place:', { origin: currentPath });
      }
      isInPlaceModeRef.current = true;
      setIsInPlaceMode(true);
      
      // Push history entry for back-button support and deep-linking
      const squareParams = new URLSearchParams();
      squareParams.set('modal', 'photo');
      squareParams.set('photo', photoId);
      if (postId) squareParams.set('post', postId);
      squareParams.set('user', username);
      if (navContext) squareParams.set('ctx', navContext);
      if (gridContext) squareParams.set('gctx', gridContext);
      
      const squareUrl = `${currentPath}?${squareParams.toString()}`;
      lastProcessedLocationRef.current = squareUrl;
      isInternalNavigationRef.current = true;
      navigate(squareUrl);
      Promise.resolve().then(() => { isInternalNavigationRef.current = false; });
      return;
    }
    
    // Profile route — use URL navigation as before
    const url = navContext === 'grid'
      ? `/profile/${username}/photos?photo=${photoId}${gridContext ? `&context=${gridContext}` : ''}`
      : isMultiPhoto 
        ? `/profile/${username}/post/${postId}?photo=${photoId}${action ? `&action=${action}` : ''}`
        : `/profile/${username}/post/${postId}/photo/${photoId}${action ? `?action=${action}` : ''}`;
    
    // Update last processed location to prevent URL monitoring from re-processing
    lastProcessedLocationRef.current = url;
    isInternalNavigationRef.current = true;
    
    if (import.meta.env.VITE_DEPLOY_ENV !== 'production') {
      logger.debug('[PhotoModalContext] Navigating to:', url);
    }
    
    navigate(url);
    
    // Reset flag after navigation
    Promise.resolve().then(() => {
      isInternalNavigationRef.current = false;
    });
  }, [navigate]);

  const closeModal = useCallback((targetUsername?: string) => {
    // Guard against URL watcher racing with close sequence
    isClosingRef.current = true;
    
    const origin = originRouteRef.current;
    const wasNonProfileOrigin = origin !== null && !isProfileRoute(origin);
    
    if (import.meta.env.VITE_DEPLOY_ENV !== 'production') {
      logger.debug('[PhotoModalContext] closeModal called:', {
        targetUsername,
        usernameFromRef: usernameRef.current,
        navigationContext: navigationContextRef.current,
        gridContextType: gridContextTypeRef.current,
        originRoute: origin,
        wasNonProfileOrigin
      });
    }
    
    // Cancel any pending URL updates
    if (pendingUrlUpdateRef.current) {
      clearTimeout(pendingUrlUpdateRef.current);
      pendingUrlUpdateRef.current = null;
    }
    
    // Clear ALL state
    setIsInPlaceMode(false);
    setIsOpen(false);
    setPhotoId(null);
    setPostId(null);
    setShouldFocusComment(false);
    setPhotoIds([]);
    setPhotoPostIds([]);
    setIsSubtabMode(false);
    setCurrentPhotoIndex(0);
    setTotalPhotos(1);
    setUsername(null);
    setNavigationContext(null);
    setGridContextType(null);
    setGridPhotosData(null);
    isInPlaceModeRef.current = false;
    originRouteRef.current = null;
    
    // If opened from a non-profile route (e.g. /square):
    // - No targetUsername → navigate back to remove modal query params
    // - With targetUsername → user explicitly wants to go to that profile
    if (wasNonProfileOrigin) {
      if (targetUsername && typeof targetUsername === 'string') {
        navigate(`/profile/${targetUsername}`);
      } else {
        // Navigate back to remove modal query params (history entry was pushed)
        navigate(origin || '/square', { replace: true });
      }
      queueMicrotask(() => { isClosingRef.current = false; });
      return;
    }
    
    // Profile origin — navigate to profile route as before
    const isGridMode = navigationContextRef.current === 'grid';
    const targetSubtab = gridContextTypeRef.current || 'all';
    const profileToNavigate = targetUsername || usernameRef.current;
    
    // Type guard: Only navigate with valid string username
    if (!profileToNavigate || typeof profileToNavigate !== 'string') {
      if (import.meta.env.VITE_DEPLOY_ENV !== 'production') {
        logger.error('[PhotoModalContext] Invalid profileToNavigate:', {
          profileToNavigate,
          type: typeof profileToNavigate,
          targetUsername,
          usernameFromRef: usernameRef.current
        });
      }
      return;
    }
    
    const profileUrl = isGridMode 
      ? `/profile/${profileToNavigate}?tab=photos&subtab=${targetSubtab}`
      : `/profile/${profileToNavigate}?tab=posts`;
    
    // CRITICAL: Update browser history IMMEDIATELY before clearing state
    window.history.replaceState(null, '', profileUrl);
    lastProcessedLocationRef.current = profileUrl;
    isInternalNavigationRef.current = true;
    
    if (import.meta.env.VITE_DEPLOY_ENV !== 'production') {
      logger.debug('[PhotoModalContext] Closing modal:', {
        isGridMode,
        profileUrl,
        currentLocation: window.location.pathname
      });
    }
    
    // Sync with React Router
    navigate(profileUrl, { replace: true });
    
    // Reset flags after effects have settled
    setTimeout(() => {
      isInternalNavigationRef.current = false;
      isClosingRef.current = false;
    }, 100);
  }, [navigate]);

  /**
   * Opens the photo modal in-place (no URL change)
   * Used for notification clicks to keep user on current page
   */
  const openModalInPlace = useCallback((
    usernameParam: string,
    photoIdParam: string,
    postIdParam: string,
    options?: { commentId?: string; replyId?: string; parentCommentId?: string }
  ) => {
    // Validate username
    if (!usernameParam || typeof usernameParam !== 'string') {
      if (import.meta.env.VITE_DEPLOY_ENV !== 'production') {
        logger.error('[PhotoModalContext] Invalid username provided to openModalInPlace:', {
          username: usernameParam,
          type: typeof usernameParam
        });
      }
      return;
    }
    
    // Set in-place mode to prevent URL monitoring from responding
    isInPlaceModeRef.current = true;
    setIsInPlaceMode(true);
    
    if (import.meta.env.VITE_DEPLOY_ENV !== 'production') {
      logger.debug('[PhotoModalContext] openModalInPlace called:', {
        username: usernameParam,
        photoId: photoIdParam,
        postId: postIdParam,
        commentId: options?.commentId
      });
    }
    
    // Set state immediately - no URL change
    setUsername(usernameParam);
    setPhotoId(photoIdParam);
    setPostId(postIdParam);
    setNavigationContext('post');
    setIsOpen(true);
    setShouldFocusComment(false);
    setGridContextType(null);
    setGridPhotosData(null);
    
    // Set target comment/reply for auto-scroll and highlight
    const commentToHighlight = options?.commentId || options?.replyId || null;
    setTargetCommentId(commentToHighlight);
    setHighlightCommentId(commentToHighlight);
    
    // Set parent comment ID for auto-expansion when navigating to a reply
    setAutoExpandParentId(options?.parentCommentId || null);
  }, []);

  /**
   * Closes the photo modal without navigation (for in-place mode)
   */
  const closeModalInPlace = useCallback(() => {
    if (import.meta.env.VITE_DEPLOY_ENV !== 'production') {
      logger.debug('[PhotoModalContext] closeModalInPlace called');
    }
    
    // Guard: prevent URL watcher from re-opening during close
    isClosingRef.current = true;
    
    // Clear state without navigation
    setIsInPlaceMode(false);
    setIsOpen(false);
    setPhotoId(null);
    setPostId(null);
    setUsername(null);
    setNavigationContext(null);
    setGridContextType(null);
    setGridPhotosData(null);
    setShouldFocusComment(false);
    setPhotoIds([]);
    setPhotoPostIds([]);
    setIsSubtabMode(false);
    setCurrentPhotoIndex(0);
    setTotalPhotos(1);
    setTargetCommentId(null);
    setHighlightCommentId(null);
    setAutoExpandParentId(null);
    
    // Reset in-place mode
    isInPlaceModeRef.current = false;
    
    // Strip query params from URL (same pattern as PostModalContext)
    const currentSearch = locationRef.current.search;
    if (currentSearch && currentSearch.includes('modal=')) {
      const cleanPath = locationRef.current.pathname;
      lastProcessedLocationRef.current = cleanPath;
      isInternalNavigationRef.current = true;
      navigate(cleanPath, { replace: true });
      queueMicrotask(() => {
        isInternalNavigationRef.current = false;
        isClosingRef.current = false;
      });
    } else {
      queueMicrotask(() => {
        isClosingRef.current = false;
      });
    }
  }, [navigate]);

  const clearFocusIntent = useCallback(() => {
    setShouldFocusComment(false);
  }, []);

  const clearHighlight = useCallback(() => {
    setHighlightCommentId(null);
  }, []);

  // Photo IDs are now passed via openModal parameter (from RPC data), no direct query needed

  const navigateToPhoto = useCallback((direction: 'prev' | 'next' | number) => {
    let newIndex: number;
    
    if (typeof direction === 'number') {
      newIndex = direction;
    } else {
      const delta = direction === 'next' ? 1 : -1;
      newIndex = currentPhotoIndex + delta;
    }
    
    if (newIndex >= 0 && newIndex < photoIds.length) {
      const newPhotoId = photoIds[newIndex];
      let newPostId = postId;
      
      // In subtab mode, update postId if navigating to a photo from a different post
      if (isSubtabMode && photoPostIds.length > newIndex) {
        newPostId = photoPostIds[newIndex];
      }
      
      // Cancel any pending URL update
      if (pendingUrlUpdateRef.current) {
        clearTimeout(pendingUrlUpdateRef.current);
      }
      
      // Update state immediately for smooth navigation
      setPhotoId(newPhotoId);
      setCurrentPhotoIndex(newIndex);
      if (newPostId !== postId) {
        setPostId(newPostId);
      }
      
      if (!isInPlaceModeRef.current) {
        // CRITICAL: Set the flag BEFORE navigate to prevent URL monitoring
        isInternalNavigationRef.current = true;
        
        // Update URL based on navigation context
        const newUrl = navigationContext === 'grid'
          ? `/profile/${username}/photos?photo=${newPhotoId}${gridContextType ? `&context=${gridContextType}` : ''}`
          : `/profile/${username}/post/${newPostId}?photo=${newPhotoId}`;
        
        // Update last processed location BEFORE updating URL
        lastProcessedLocationRef.current = newUrl;
        
        // Use navigate with skipEffect flag to prevent URL monitoring from processing
        navigate(newUrl, { 
          replace: true, 
          state: { skipEffect: true } 
        });
        
        // Reset the flag after a short delay to ensure React Router has processed the navigation
        setTimeout(() => {
          isInternalNavigationRef.current = false;
        }, 50);
      } else {
        // In-place mode (e.g., /square) — update query params without new history entry
        const currentParams = new URLSearchParams(locationRef.current.search);
        currentParams.set('photo', newPhotoId);
        if (newPostId) currentParams.set('post', newPostId);
        const updatedUrl = `${locationRef.current.pathname}?${currentParams.toString()}`;
        lastProcessedLocationRef.current = updatedUrl;
        isInternalNavigationRef.current = true;
        navigate(updatedUrl, { replace: true, state: { skipEffect: true } });
        setTimeout(() => { isInternalNavigationRef.current = false; }, 50);
      }
    }
  }, [currentPhotoIndex, photoIds, photoPostIds, isSubtabMode, username, postId, navigationContext, gridContextType, navigate]);

  // Sync photo context from fetched post data (handles page refresh for multi-photo posts)
  const syncPhotoContext = useCallback((newPhotoIds: string[], currentPhotoId?: string) => {
    // Only sync if:
    // 1. New data is available
    // 2. Context is stale (photoIds empty - meaning from page refresh)
    // 3. We're in post context (not grid - extra safety)
    if (
      newPhotoIds.length > 0 && 
      photoIds.length === 0 && 
      (navigationContext === 'post' || navigationContext === 'grid' || navigationContext === null)
    ) {
      setPhotoIds(newPhotoIds);
      setTotalPhotos(newPhotoIds.length);
      
      // Calculate correct index from currentPhotoId
      if (currentPhotoId) {
        const idx = newPhotoIds.indexOf(currentPhotoId);
        if (idx >= 0) {
          setCurrentPhotoIndex(idx);
        }
      }
      
      if (import.meta.env.VITE_DEPLOY_ENV !== 'production') {
        logger.debug('[PhotoModalContext] Synced photo context from post data:', {
          photoIds: newPhotoIds,
          totalPhotos: newPhotoIds.length,
          currentPhotoId,
          calculatedIndex: currentPhotoId ? newPhotoIds.indexOf(currentPhotoId) : 0
        });
      }
    }
  }, [photoIds.length, navigationContext]);

  const value: PhotoModalContextType = {
    isOpen,
    photoId,
    postId,
    username,
    isInPlaceMode,
    shouldFocusComment,
    currentPhotoIndex,
    totalPhotos,
    photoIds,
    navigationContext,
    gridContextType,
    gridPhotosData,
    targetCommentId,
    highlightCommentId,
    autoExpandParentId,
    openModal,
    closeModal,
    openModalInPlace,
    closeModalInPlace,
    navigateToPhoto,
    clearFocusIntent,
    clearHighlight,
    syncPhotoContext
  };

  return (
    <PhotoModalContext.Provider value={value}>
      {children}
    </PhotoModalContext.Provider>
  );
};
